import heroImage from "./article_pics/earthworm_lab_tile.png";

const article = {
    title: "Building an EarthWorm Reverse SOCKS Lab",
    published: "2026-05-09",
    readingTime: "12 min read",
    tags: ["PCAP", "Suricata", "Zeek", "Threat Emulation", "Docker"],
    summary:
        "This lab uses the real EarthWorm binary inside isolated containers so you can collect clean PCAPs, inspect the setup sequence, and test detections without exposing the tool on your host.",
    favorite: false,
    image: heroImage,
    repoUrl: "https://github.com/Pb-22/EarthWorm-Lab",
    content: `
# Building an EarthWorm Reverse SOCKS Lab

EarthWorm is a small tunneling utility that supports reverse SOCKS, port forwarding, and chained transfers.[1] Public reporting still shows it appearing in modern intrusion sets, especially in China aligned operations where operators need a simple way to move from a foothold to deeper network access.[2][3][4] In 2019 the original author stopped updating the project after seeing it abused in real attacks, but the tool still appears in current threat reporting.[1]

This article walks through a small local lab built around the real EarthWorm binary. The goal is simple. Stand up a safe local lab, capture clean PCAPs, and inspect the setup stage and request stage traffic that can support network detection work.

## Why build this lab

EarthWorm matters because it gives an operator a practical way to create an outbound tunnel from an internal host to an external server. That helps bypass simple inbound firewall rules and NAT boundaries. Cisco Talos documented UAT 8837 using multiple EarthWorm variants to find one that would evade endpoint tooling and then create reverse tunnels back to attacker infrastructure.[2] ESET documented BackdoorDiplomacy using EarthWorm as one of several open source tools for reconnaissance and lateral movement, with some of those tools wrapped in VMProtect.[3] Sygnia also documented the use of SAMRID, identified as EarthWorm, in a Velvet Ant intrusion and noted that the tool has been used by other China aligned groups as well.[4]

That makes EarthWorm worth studying from a detection engineering point of view.

## What this lab does

The lab uses Docker containers and keeps the real EarthWorm binary off the host. It provides:

- a public looking server at \`203.0.113.10\`
- an internal client at \`10.1.1.5\`
- a router path between them
- a target web server at \`10.1.1.50\`
- a browser UI at \`http://127.0.0.1:23456\`
- one click PCAP capture and download

The lab uses the standard reverse SOCKS pattern from the official EarthWorm documentation:[1]

- server side: \`ew -s rcsocks -l 1080 -e 8888\`
- client side: \`ew -s rssocks -d <server_ip> -e 8888\`

The UI and capture helpers are our wrapper. The EarthWorm tunnel traffic itself is not modified.

![EarthWorm lab dashboard](${require("./article_pics/earthworm_lab_dashboard.png")})

*The dashboard shows the client, router, server, capture state, and the saved PCAP list.*

## Build the lab

The project is designed to build itself with Docker Compose.

1. Clone or copy the lab files into a working directory.
2. Run \`docker compose up -d --build\`.
3. Open \`http://127.0.0.1:23456\`.
4. Use the browser UI to reset state, start the server, connect the client, capture traffic, and download PCAPs.

The UI includes a **Reset Lab State** button so each run starts from a clean baseline.

## Collect the first handshake PCAP

The first useful capture is the handshake oriented PCAP.

1. Click **Reset Lab State**.
2. Click **Initialize Routing**.
3. Click **Start Server**.
4. Click **Start Capture**.
5. Click **Connect Client**.
6. Wait for the tunnel connected indicator.
7. Click **Test SOCKS Through Tunnel** once.
8. Click **Stop Capture / Save**.
9. Download the PCAP.

This capture includes both the initial setup traffic and one tunneled HTTP request.

## Collect the post setup request PCAP

The second useful capture starts after the control channel is already up.

1. Click **Reset Lab State**.
2. Click **Initialize Routing**.
3. Click **Start Server**.
4. Click **Connect Client**.
5. Wait for the tunnel connected indicator.
6. Click **Start Capture**.
7. Click **Test SOCKS Through Tunnel** once or more.
8. Click **Stop Capture / Save**.
9. Download the PCAP.

This capture focuses on request stage traffic after setup has already completed.

![Saved PCAPs in the lab UI](${require("./article_pics/earthworm_lab_downloads.png")})

*The downloads section lets you save or delete test PCAPs after each run.*

## What to look for in the PCAPs

The most useful bytes are in the early control and request stages.

### Setup stage control stream

In fresh setup captures I consistently observed:

- \`01 01 00 00 00 00\`
- \`01 02 00 00 00 00\`
- often \`01 03 00 00 00 00\`

### Request stage stream

When I drove a new tunneled request, I consistently observed:

- \`01 04 00 00 00 00\`
- \`01 05 00 00 00 00\`
- then SOCKS negotiation bytes such as:
  - \`05 02 00 01\`
  - \`05 00\`
  - \`05 01 00 01 ...\`

The key point is that the strongest EarthWorm looking behavior is front loaded. After that, the traffic becomes more like normal SOCKS plus the tunneled application data.

![Wireshark view of the setup stage sequence](${require("./article_pics/earthworm_lab_setup_sequence.png")})

*The setup stage stream is where the \`01 01\`, \`01 02\`, and often \`01 03\` control records appear.*

![Wireshark view of the request stage sequence](${require("./article_pics/earthworm_lab_request_sequence.png")})

*The request stage stream shows \`01 04\`, \`01 05\`, then SOCKS negotiation and the tunneled HTTP request.*

## What this means for detection work

I would split detections by communication stage.

### Setup stage detection

This is the stronger detection family. It focuses on the early control sequence on a fresh TCP connection.

A practical name is:

- **EARTHWORM Setup Stage Control Sequence**

### Post setup request stage detection

This catches request activity after the control channel is already established.

A practical name is:

- **EARTHWORM Post Setup Request Stage SOCKS Sequence**

This split matters because one detector may catch the start of a session while another may catch activity after setup was missed.

## Closing thoughts

This lab is intentionally small. It is not trying to be a complete command and control platform. It is a repeatable way to generate EarthWorm traffic, collect PCAPs, and study the parts of the protocol that are useful for network detection.

It also gives you a safe place to test ideas before moving on to Suricata, Zeek, or Splunk logic.

## Sources

[1] rootkiter, "EarthWorm" official documentation and stop update notice, accessed May 9 2026: <https://rootkiter.com/EarthWorm/en/index>

[2] Cisco Talos, "UAT 8837 targets critical infrastructure sectors in North America," January 15 2026: <https://blog.talosintelligence.com/uat-8837/>

[3] ESET Research, "BackdoorDiplomacy: Upgrading from Quarian to Turian," June 10 2021: <https://www.welivesecurity.com/2021/06/10/backdoordiplomacy-upgrading-quarian-turian/>

[4] Sygnia, "In Depth Analysis: Velvet Ant's Prolonged Cyber Attack on a Large Organization," June 3 2024: <https://www.sygnia.co/blog/china-nexus-threat-group-velvet-ant/>
`,
};

export default article;
